import { useEffect, useState, type ReactNode } from "react";
import { Link2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { detectPlatform } from "../lib/platform";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import {
  HostAuthError,
  isInternationalHost,
  normalizeAccountLinkCode,
  signInWithInternationalHost,
  type InternationalHost,
} from "../lib/internationalHost";

// eslint-disable-next-line react-refresh/only-export-components
export function hostLabel(host: InternationalHost): string {
  switch (host) {
    case "line":
      return "LINE";
    case "discord":
      return "Discord";
    case "max":
      return "MAX";
    case "vk":
      return "VK";
    case "teams":
      return "Microsoft Teams";
  }
}

function errorKey(error: unknown): string {
  if (error instanceof HostAuthError) return `hostAuth.errors.${error.code}`;
  if (error instanceof Error && error.message.includes("not configured")) {
    return "hostAuth.errors.provider_not_configured";
  }
  return "hostAuth.errors.generic";
}

export function InternationalHostAuthGate({ children }: { children: ReactNode }) {
  const platform = detectPlatform();
  const isPublicLegalRoute =
    typeof window !== "undefined" && window.location.pathname === "/privacy";
  const isPublicShareRoute =
    typeof window !== "undefined" && /^\/s\/[A-Za-z0-9_-]{22}$/.test(window.location.pathname);
  if (isPublicLegalRoute || isPublicShareRoute || !isSupabaseConfigured || !isInternationalHost(platform)) {
    return <>{children}</>;
  }
  return <InternationalHostAuthGateInner host={platform}>{children}</InternationalHostAuthGateInner>;
}

function InternationalHostAuthGateInner({
  host,
  children,
}: {
  host: InternationalHost;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const [continueLocal, setContinueLocal] = useState(false);
  const [mode, setMode] = useState<"choice" | "link">("choice");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) setHasSession(Boolean(data.session));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) setHasSession(Boolean(session));
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  if (hasSession || continueLocal) return <>{children}</>;
  if (hasSession === null) {
    return (
      <div
        role="status"
        aria-label={t("common.loading")}
        className="host-surface bg-slate-50 dark:bg-slate-950"
      />
    );
  }

  const submit = async (linkCode?: string) => {
    setPending(true);
    setError(null);
    try {
      await signInWithInternationalHost(host, linkCode);
    } catch (authError) {
      if (authError instanceof Error && authError.message === "line_login_redirect") return;
      setError(t(errorKey(authError)));
    } finally {
      setPending(false);
    }
  };

  const normalizedCode = normalizeAccountLinkCode(code);
  return (
    <main className="host-surface flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <section className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-indigo-100 p-3 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
              {hostLabel(host)}
            </p>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              {t("hostAuth.title")}
            </h1>
          </div>
        </div>

        {mode === "choice" ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">{t("hostAuth.choiceBody")}</p>
            <button
              type="button"
              disabled={pending}
              onClick={() => void submit()}
              className="mt-5 w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {pending ? t("hostAuth.signingIn") : t("hostAuth.newAccount")}
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                setError(null);
                setMode("link");
              }}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <Link2 className="h-4 w-4" />
              {t("hostAuth.existingAccount")}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">{t("hostAuth.linkBody")}</p>
            <label className="mt-5 block text-sm font-medium text-slate-800 dark:text-slate-200">
              {t("hostAuth.codeLabel")}
            </label>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              maxLength={11}
              placeholder="ABCD-EFGH"
              className="mt-2 w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-center font-mono text-xl font-semibold uppercase tracking-[0.18em] text-slate-900 focus:border-indigo-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
            <button
              type="button"
              disabled={pending || normalizedCode.length !== 8}
              onClick={() => void submit(normalizedCode)}
              className="mt-4 w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {pending ? t("hostAuth.signingIn") : t("hostAuth.linkAndContinue")}
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => setMode("choice")}
              className="mt-3 min-h-11 w-full px-4 py-2 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
              {t("common.back")}
            </button>
          </>
        )}

        {error && <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button
          type="button"
          disabled={pending}
          onClick={() => setContinueLocal(true)}
          className="mt-5 min-h-11 w-full text-xs text-slate-600 underline-offset-2 hover:underline dark:text-slate-300"
        >
          {t("hostAuth.continueLocal")}
        </button>
      </section>
    </main>
  );
}
